import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, AlertCircle, MessageSquare, Loader2 } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { submitContactForm, createGrievance } from '../services/api';
import toast from 'react-hot-toast';

const ContactPage = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [submittingContact, setSubmittingContact] = useState(false);
  const [submittingGrievance, setSubmittingGrievance] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  
  const [grievanceForm, setGrievanceForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: 'General',
    priority: 'Medium',
    subject: '',
    description: '',
    isUrgent: false
  });

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    // Listen for custom language change events
    window.addEventListener('languageChanged', handleLanguageChange);
    
    // Listen for storage changes (for cross-tab synchronization)
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!contactForm.name.trim()) {
      toast.error(getText('contact.form.name') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!contactForm.email.trim()) {
      toast.error(getText('contact.form.email') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!contactForm.subject.trim()) {
      toast.error(getText('contact.form.subject') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!contactForm.message.trim()) {
      toast.error(getText('contact.form.message') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    
    setSubmittingContact(true);
    try {
      await submitContactForm(contactForm);
      toast.success(getText('contact.form.success'));
      setContactForm({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message || getText('contact.form.error');
      toast.error(errorMessage);
    } finally {
      setSubmittingContact(false);
    }
  };

  const handleGrievanceSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!grievanceForm.name.trim()) {
      toast.error(getText('grievance.form.name') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!grievanceForm.email.trim()) {
      toast.error(getText('grievance.form.email') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!grievanceForm.phone.trim()) {
      toast.error(getText('grievance.form.phone') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!grievanceForm.subject.trim()) {
      toast.error(getText('grievance.form.subjectLabel') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    if (!grievanceForm.description.trim()) {
      toast.error(getText('grievance.form.descriptionLabel') + ' ' + (getText('registration.required') || 'is required'));
      return;
    }
    
    setSubmittingGrievance(true);
    try {
      await createGrievance({
        name: grievanceForm.name,
        email: grievanceForm.email,
        phone: grievanceForm.phone,
        company: grievanceForm.company,
        category: grievanceForm.category,
        priority: grievanceForm.priority,
        subject: grievanceForm.subject,
        description: grievanceForm.description,
        isUrgent: grievanceForm.isUrgent
      });
      toast.success(getText('grievance.form.success'));
      setGrievanceForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        category: 'General',
        priority: 'Medium',
        subject: '',
        description: '',
        isUrgent: false
      });
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message || getText('grievance.form.error');
      toast.error(errorMessage);
    } finally {
      setSubmittingGrievance(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleGrievanceChange = (e) => {
    setGrievanceForm({
      ...grievanceForm,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = {
    address: 'IFM Building, Industrial Zone, Mumbai, Maharashtra 400001',
    phone: '+91 22 1234 5678',
    email: 'info@ifm.gov.in',
    hours: 'Monday - Friday: 9:00 AM - 6:00 PM'
  };

  const departments = [
    {
      name: 'MPCB Support',
      email: 'mpcb@ifm.gov.in',
      phone: '+91 22 1234 5679'
    },
    {
      name: 'DISH Safety',
      email: 'dish@ifm.gov.in',
      phone: '+91 22 1234 5680'
    },
    {
      name: 'DIC Programs',
      email: 'dic@ifm.gov.in',
      phone: '+91 22 1234 5681'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('contact.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="section py-16 bg-gray-50">
        <div className="container-medium">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {getText('contact.info.address')}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {getText('contact.info.address.value')}
              </p>
            </div>
            
            <div className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-emerald-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Phone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                {getText('contact.info.phone')}
              </h3>
              <p className="text-gray-600 text-sm">{getText('contact.info.phone.value1')}</p>
            </div>
            
            <div className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                {getText('contact.info.email')}
              </h3>
              <p className="text-gray-600 text-sm">{getText('contact.info.email.value1')}</p>
              <p className="text-gray-600 text-sm">{getText('contact.info.email.value2')}</p>
            </div>
            
            <div className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                {getText('contact.info.hours')}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {getText('contact.info.hours.monFri')}<br />
                {getText('contact.info.hours.sat')}<br />
                {getText('contact.info.hours.sun')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="section py-16 bg-gray-100">
        <div className="container-medium">
          <div className="max-w-4xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex space-x-4 mb-8 bg-white border-2 border-gray-200 rounded-2xl p-2 shadow-md">
              <button
                onClick={() => setActiveTab('contact')}
                className={`flex-1 px-6 py-4 rounded-xl font-bold text-base transition-all duration-300 flex items-center justify-center gap-3 ${
                  activeTab === 'contact'
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <MessageCircle className="h-5 w-5" />
                {getText('contact.form.contactTab')}
              </button>
              <button
                onClick={() => setActiveTab('grievance')}
                className={`flex-1 px-6 py-4 rounded-xl font-bold text-base transition-all duration-300 flex items-center justify-center gap-3 ${
                  activeTab === 'grievance'
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <AlertCircle className="h-5 w-5" />
                {getText('contact.form.grievanceTab')}
              </button>
            </div>
            
            {/* Contact Form */}
            {activeTab === 'contact' && (
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl border-2 border-gray-200 p-8 md:p-10">
                <p className="text-base md:text-lg text-gray-800 font-semibold text-center mb-8">
                  {getText('contact.form.subtitle')}
                </p>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('contact.form.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white font-medium"
                        placeholder={getText('contact.form.name')}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('contact.form.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white font-medium"
                        placeholder={getText('contact.form.email')}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('contact.form.phone')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white font-medium"
                        placeholder={getText('contact.form.phone')}
                        required
                        maxLength={15}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('contact.form.company')}
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={contactForm.company}
                        onChange={handleContactChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white font-medium"
                        placeholder={getText('contact.form.company')}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {getText('contact.form.subject')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleContactChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white font-medium"
                      placeholder={getText('contact.form.subject')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {getText('contact.form.message')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none bg-white font-medium"
                      rows={6}
                      placeholder={getText('contact.form.message')}
                      required
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submittingContact}
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-lg md:text-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {submittingContact ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {getText('contact.form.submitting') || 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        {getText('contact.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
            
            {/* Grievance Form */}
            {activeTab === 'grievance' && (
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl border-2 border-gray-200 p-8 md:p-10">
                <p className="text-base md:text-lg text-gray-800 font-semibold text-center mb-8">
                  {getText('grievance.form.subtitle')}
                </p>
                <form onSubmit={handleGrievanceSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={grievanceForm.name}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        placeholder={getText('grievance.form.name')}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={grievanceForm.email}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        placeholder={getText('grievance.form.email')}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.phone')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={grievanceForm.phone}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        placeholder={getText('grievance.form.phone')}
                        required
                        maxLength={15}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.company')}
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={grievanceForm.company}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        placeholder={getText('grievance.form.company')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.category')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="category"
                        value={grievanceForm.category}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        required
                      >
                        <option value="General">{getText('grievance.category.general')}</option>
                        <option value="Labour">{getText('grievance.category.labour')}</option>
                        <option value="Environmental">{getText('grievance.category.environmental')}</option>
                        <option value="Infrastructure">{getText('grievance.category.infrastructure')}</option>
                        <option value="Compliance">{getText('grievance.category.compliance')}</option>
                        <option value="Technical">{getText('grievance.category.technical')}</option>
                        <option value="Safety">{getText('grievance.category.safety')}</option>
                        <option value="Pollution">{getText('grievance.category.pollution')}</option>
                        <option value="Land">{getText('grievance.category.land')}</option>
                        <option value="Electricity">{getText('grievance.category.electricity')}</option>
                        <option value="Water">{getText('grievance.category.water')}</option>
                        <option value="Roads">{getText('grievance.category.roads')}</option>
                        <option value="Waste">{getText('grievance.category.waste')}</option>
                        <option value="Pricing">{getText('grievance.category.pricing')}</option>
                        <option value="Other">{getText('grievance.category.other')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {getText('grievance.form.priority')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="priority"
                        value={grievanceForm.priority}
                        onChange={handleGrievanceChange}
                        className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                        required
                      >
                        <option value="Low">{getText('grievance.priority.low')}</option>
                        <option value="Medium">{getText('grievance.priority.medium')}</option>
                        <option value="High">{getText('grievance.priority.high')}</option>
                        <option value="Urgent">{getText('grievance.priority.urgent')}</option>
                        <option value="Critical">{getText('grievance.priority.critical')}</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {getText('grievance.form.subjectLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={grievanceForm.subject}
                      onChange={handleGrievanceChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white font-medium"
                      placeholder={getText('contact.form.subject')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {getText('grievance.form.descriptionLabel')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={grievanceForm.description}
                      onChange={handleGrievanceChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none bg-white font-medium"
                      rows={6}
                      required
                      placeholder={getText('grievance.form.descriptionPlaceholder')}
                    />
                  </div>
                  
                  <div className="flex items-center p-5 bg-emerald-50 rounded-xl border-2 border-emerald-200">
                    <input
                      type="checkbox"
                      name="isUrgent"
                      checked={grievanceForm.isUrgent}
                      onChange={(e) => setGrievanceForm({...grievanceForm, isUrgent: e.target.checked})}
                      className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label className="ml-3 block text-sm font-semibold text-gray-900">
                      {getText('grievance.form.urgentLabel')}
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submittingGrievance}
                    className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg md:text-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {submittingGrievance ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {getText('grievance.form.submitting') || 'Submitting...'}
                      </>
                    ) : (
                      <>
                        <MessageSquare className="h-5 w-5" />
                        {getText('grievance.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
